import { useEffect, useState, type FormEvent } from 'react'
import { Notice } from '@shared/ui/Notice'
import { useAccountProfile } from '@features/account/hooks/useAccountProfile'
import { useUserPreferences } from '@features/preferences/hooks/useUserPreferences'
import { AVATAR_COLOR_VALUES, type AvatarColorPreference } from '@features/preferences/types'
import { useTranslation } from '@/i18n'
import { ChangeEmailSection } from './ChangeEmailSection'

/**
 * Settings § account: プロファイル編集 (T-G7、T-H3)。
 *
 * - name のみインライン編集可。空文字 → null (UI 上の「クリア」)
 * - email は read-only。変更したい場合は「変更」ボタンで ChangeEmailSection を開く
 *   (新 email + 現マスターパスワード → 確認メール送信)
 * - アバターは画像アップロード非対応。preferences.avatarColor で色のみカスタマイズ
 */

export const AccountSection = (): JSX.Element => {
  const { t } = useTranslation()
  const { state, lastErrorKey, saving, saved, save, refresh } = useAccountProfile()
  const { preferences, setPreference } = useUserPreferences()
  const [name, setName] = useState('')
  const [changeEmailOpen, setChangeEmailOpen] = useState(false)
  const [avatarErrorKey, setAvatarErrorKey] = useState<string | null>(null)

  // アバター色クリック: 失敗時は楽観更新が revert されるため、無言にせず通知する。
  const onPickAvatarColor = (color: AvatarColorPreference): void => {
    setAvatarErrorKey(null)
    void setPreference('avatarColor', color).catch(() => {
      setAvatarErrorKey('settings.account.avatar_save_failed')
    })
  }

  useEffect(() => {
    if (state.kind === 'ready') {
      setName(state.profile.name ?? '')
    }
  }, [state])

  // アバターに表示する 1 文字 (name → email の先頭、大文字)。
  const avatarInitial =
    state.kind === 'ready'
      ? (state.profile.name?.trim()[0] ?? state.profile.email[0] ?? '?').toUpperCase()
      : '?'

  const onSubmit = (ev: FormEvent<HTMLFormElement>): void => {
    ev.preventDefault()
    void save(name.trim())
  }

  if (state.kind === 'loading') {
    return <div className="settings-item-sub">{t('settings.account.loading')}</div>
  }

  return (
    <>
      <form className="card settings-form-card" onSubmit={onSubmit} data-testid="account-form">
        {lastErrorKey !== null && (
          <Notice tone="danger" ico="alert">
            {t(lastErrorKey)}
          </Notice>
        )}

        {saved && (
          <Notice tone="info" ico="check">
            {t('settings.account.saved')}
          </Notice>
        )}

        {state.kind === 'ready' && state.profile.emailVerifiedAt === null && (
          <Notice tone="warn" ico="alert">
            {t('settings.account.email_unverified_warning', { email: state.profile.email })}
          </Notice>
        )}

        <div className="settings-avatar-row">
          <div
            className="settings-avatar"
            data-avatar-color={preferences.avatarColor}
            aria-hidden="true"
          >
            {avatarInitial}
          </div>
          <div className="settings-avatar-customize">
            <div className="settings-avatar-title">{t('settings.account.avatar_title')}</div>
            <div className="settings-avatar-hint">{t('settings.account.avatar_color_label')}</div>
            {avatarErrorKey !== null && (
              <div className="settings-avatar-error">{t(avatarErrorKey)}</div>
            )}
            <div
              className="avatar-swatch-row"
              role="group"
              aria-label={t('settings.account.avatar_color_label')}
            >
              {AVATAR_COLOR_VALUES.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`avatar-swatch${preferences.avatarColor === color ? ' is-active' : ''}`}
                  data-avatar-color={color}
                  aria-pressed={preferences.avatarColor === color}
                  aria-label={color}
                  data-testid={`avatar-swatch-${color}`}
                  onClick={() => onPickAvatarColor(color)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="account-name">
            {t('settings.account.name')}
          </label>
          <input
            id="account-name"
            className="input"
            type="text"
            value={name}
            onChange={ev => setName(ev.target.value)}
            maxLength={80}
            data-testid="account-name-input"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="account-email">
            {t('settings.account.email')}
          </label>
          <div className="settings-account-email-row">
            <input
              id="account-email"
              className="input"
              type="email"
              value={state.kind === 'ready' ? state.profile.email : ''}
              disabled
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setChangeEmailOpen(v => !v)}
              aria-expanded={changeEmailOpen}
              data-testid="change-email-toggle"
            >
              {changeEmailOpen
                ? t('settings.account.change_email.cancel')
                : t('settings.account.change_email.button')}
            </button>
          </div>
        </div>

        <div className="settings-form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            data-testid="account-save"
          >
            {saving ? t('settings.account.saving') : t('settings.account.save')}
          </button>
        </div>
      </form>

      {changeEmailOpen && (
        <ChangeEmailSection
          onClose={() => {
            setChangeEmailOpen(false)
            void refresh()
          }}
        />
      )}
    </>
  )
}
